import React from 'react'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import GlobeShowcase from './GlobeShowcase'
import { Button } from './ui'
import { cn } from '../utils'

const Hero: React.FC = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white dark:bg-gray-950">
      {/* Minimal geometric backdrop — dot grid + soft brand glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className={cn(
            'absolute inset-0 opacity-[0.35] dark:opacity-[0.18]'
          )}
          style={{
            backgroundImage:
              'radial-gradient(currentColor 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            color: 'rgba(31, 77, 132, 0.18)',
            maskImage:
              'radial-gradient(ellipse 70% 60% at 50% 50%, black 40%, transparent 100%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 70% 60% at 50% 50%, black 40%, transparent 100%)',
          }}
        />
        <div
          className={cn(
            'absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full blur-3xl',
            'bg-primary-100/60 dark:bg-primary-900/25'
          )}
        />
        <div
          className={cn(
            'absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full blur-3xl',
            'bg-secondary-100/70 dark:bg-secondary-900/25'
          )}
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 text-center lg:text-left"
          >
            {/* Wordmark + motto */}
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <span className="inline-block h-px w-8 bg-primary-400" aria-hidden="true" />
              <p className="text-xs sm:text-sm font-medium tracking-[0.22em] uppercase text-gray-500 dark:text-gray-400">
                MATNO · From Insight to Impact
              </p>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] font-bold tracking-tight leading-[1.05] text-gray-900 dark:text-white mb-6">
              Helping Africa turn{' '}
              <span className="text-secondary-500 dark:text-secondary-300">data</span>,{' '}
              <span className="text-secondary-500 dark:text-secondary-300">AI</span>, and{' '}
              <span className="text-primary-500 dark:text-primary-300">digital transformation</span>{' '}
              into inclusive development impact.
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 font-light mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              An Africa-focused think-and-do tank working at the intersection of research,
              policy, technology, and implementation.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button
                as="link"
                to="/contact#partner"
                variant="primary"
                size="lg"
                icon={<ArrowRight className="w-5 h-5" />}
              >
                Partner with MATNO
              </Button>
              <Button
                as="a"
                href="#pillars"
                variant="outline"
                size="lg"
              >
                Explore our work
              </Button>
            </div>
          </motion.div>

          {/* Right: Foreground interactive globe */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative w-full aspect-square max-w-[520px] mx-auto lg:max-w-none"
          >
            <GlobeShowcase />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Hero
